
import { Vue, Component, Prop } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import S3BucketMixin from "@/mixins/storage/S3BucketMixins";

import {
    Conversation,
    Custom_Request,
    User
} from "@/generated/graphql";

import GET_CONVERSATION from "@/apollo/conversations/GetConversation.gql";
import LOAD_CONVERSATION from "@/apollo/requests/GetConversation.gql";
import CREATE_CONVERSATION from "@/apollo/conversations/CreateConversation.gql";
import CREATE_MESSAGE from "@/apollo/conversations/CreateMesssage.gql";
import UPDATE_CUSTOM_REQUEST from "@/apollo/requests/UpdateCustomRequest.gql";

import { v4 as uuid } from "uuid";
import splitFileNameExt from "@/utils/splitFileNameExt";
import InboxWindow from "@/components/inbox/InboxWindow.vue";
import UiButton from "@/components/button/ui-button.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import EmptyListImageInbox from "@/components/icons/emptyListImages/EmptyListImageInbox.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";

interface NewMessage {
    attachment: File;
    content: string;
    id: string;
}

@Component({
    components: {
        InboxWindow,
        UiButton,
        EmptyListPlaceholder,
        EmptyListImageInbox,
        PlusIcon,
        LoadingOverlay
    },
    apollo: {
        conversation: {
            query: LOAD_CONVERSATION,
            variables() {
                return {
                    id: this.request?.conversation?.id,
                    currentUserId: this.currentUser.id
                };
            },
            skip() {
                return !this.request?.conversation?.id
            },
            update(data) {
                return data.conversation_by_pk;
            },
        },
        $subscribe: {
            conversation: {
                query: GET_CONVERSATION,
                variables() {
                    return {
                        id: this.request?.conversation?.id,
                        currentUserId: this.currentUser.id
                    };
                },
                skip() {
                    return !this.request?.conversation?.id
                },
                update(data) {
                    return data.conversation_by_pk;
                },
            },
        }
    }
})
export default class Messages extends mixins(S3BucketMixin) {
    @Prop({ type: Object, default: undefined }) readonly request!: Custom_Request;

    conversation: Conversation = undefined;
    currentUser: User = Vue.prototype.$currentUser;
    uploadingAttachmentId: string = "";
    locallyCreated: boolean = false;
    noConversationsPlaceholder: string = "No conversations for now";

    async sendMessage({ attachment, content, id }: NewMessage) {
        if (!attachment) {
            return await this._createMessage(content);
        }

        const uid = uuid();
        const { ext, name } = splitFileNameExt(attachment.name);
        const filename = `${name}-${uid}.${ext}`;
        this.uploadingAttachmentId = id;

        await this.setFile(filename, attachment);
        await this._createMessage(content, filename);
    }

    async _createConversation(): Promise<string> {
        const object = {
            users: {
                data: [{ user_id: this.request.created_by.id }, { user_id: this.request.company.admins[0].user_id }],
            },
            is_custom_request_conversation: true,
        };
        this.locallyCreated = true;
        const created = await this.$apollo.mutate({
            mutation: CREATE_CONVERSATION,
            variables: { object },
        });
        await this.$apollo.mutate({
            mutation: UPDATE_CUSTOM_REQUEST,
            variables: {
                id: this.request.id,
                set: {
                    conversation_id: created.data.insert_conversation_one.id
                }
            }
        })
        this.$root.$emit("refetchRequest");
        return created.data.insert_conversation_one.id;
    }

    async _createMessage(message: string, key?: string) {
        if (!this.conversation) return;
        const recipientMessage = this.conversation.users
            .filter(user => user.user.id !== this.currentUser.id)
            .map(user => ({
                recipient_id: user.user.id,
                conversation_id: this.conversation!.id,
            }));

        const object = {
            attachment: key || null,
            content: message || null,
            conversation_id: this.conversation.id,
            sender_id: this.currentUser.id,
            recipient_message: { data: recipientMessage },
        };

        return this.$apollo.mutate({
            mutation: CREATE_MESSAGE,
            variables: { object },
        });
    }
}
